import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StreamingServicesContext = createContext({ services: [], getLogo: () => null, refresh: () => {} });

export const useStreamingServices = () => useContext(StreamingServicesContext);

export const StreamingServicesProvider = ({ children, user }) => {
  const [services, setServices] = useState([]);

  const refresh = useCallback(async () => {
    if (!user) { setServices([]); return; }
    try {
      const res = await axios.get(`${API}/streaming-services`, { withCredentials: true });
      setServices(res.data || []);
    } catch (_) { /* non-fatal */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Build a name → logo_path lookup (case-insensitive)
  const logoMap = useMemo(() => {
    const map = {};
    services.forEach((s) => {
      if (s && s.name && s.logo_path) {
        map[s.name.toLowerCase()] = s.logo_path;
      }
    });
    return map;
  }, [services]);

  const getLogo = useCallback((name) => {
    if (!name) return null;
    const path = logoMap[String(name).toLowerCase()];
    if (!path) return null;
    return `https://image.tmdb.org/t/p/w45${path}`;
  }, [logoMap]);

  return (
    <StreamingServicesContext.Provider value={{ services, getLogo, refresh }}>
      {children}
    </StreamingServicesContext.Provider>
  );
};

/**
 * Renders a streaming-service label with its TMDB-sourced logo (when available).
 * Falls back gracefully to plain text. Pass `prefix` to render a prefix character
 * (e.g. "📺 ") only when no logo is available.
 */
export const ServiceLabel = ({ name, prefix = '', size = 16, className = '' }) => {
  const { getLogo } = useStreamingServices();
  if (!name) return null;
  const logo = getLogo(name);
  return (
    <span className={`service-label ${className}`} data-testid="service-label">
      {logo ? (
        <img
          src={logo}
          alt=""
          className="service-label-logo"
          style={{ height: size, width: size, objectFit: 'contain', borderRadius: 3 }}
          loading="lazy"
        />
      ) : prefix ? (
        <span aria-hidden="true">{prefix}</span>
      ) : null}
      <span className="service-label-name">{name}</span>
    </span>
  );
};

export default StreamingServicesContext;
